import api from './api';

/**
 * Chat Service
 * Handles all chat/messaging-related API calls
 */
const chatService = {
    /**
     * Get all conversations
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getConversations: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/chat/conversations${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get or create conversation with user
     * @param {string} userId - User ID to chat with
     * @returns {Promise<Object>}
     */
    getOrCreateConversation: async (userId) => {
        return api.post('/user/chat/conversations', { participantId: userId });
    },

    /**
     * Start conversation with a user (requires follow relationship)
     * @param {string} userId - User ID to chat with
     * @returns {Promise<Object>}
     */
    startConversation: async (userId) => {
        return api.post('/user/chat/conversations', { recipientId: userId });
    },

    /**
     * Get messages in conversation
     * @param {string} conversationId - Conversation ID
     * @param {Object} params - Query parameters (page, limit)
     * @returns {Promise<Object>}
     */
    getMessages: async (conversationId, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/chat/conversations/${conversationId}/messages${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Send text message
     * @param {string} conversationId - Conversation ID
     * @param {Object} data - Message data (text, type, replyTo)
     * @returns {Promise<Object>}
     */
    sendMessage: async (conversationId, data) => {
        return api.post(`/user/chat/conversations/${conversationId}/messages`, data);
    },

    /**
     * Send message with attachments (images, videos, audio, files)
     * @param {string} conversationId - Conversation ID
     * @param {FormData} formData - FormData with text and attachments
     * @returns {Promise<Object>}
     */
    sendMessageWithAttachments: async (conversationId, formData) => {
        return api.post(`/user/chat/conversations/${conversationId}/messages`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },

    /**
     * Mark messages as read
     * @param {string} conversationId - Conversation ID
     * @returns {Promise<Object>}
     */
    markAsRead: async (conversationId) => {
        return api.patch(`/user/chat/conversations/${conversationId}/read`);
    },

    /**
     * Get unread messages count
     * @returns {Promise<Object>}
     */
    getUnreadCount: async () => {
        return api.get('/user/chat/unread-count');
    }
};

export default chatService;
